/**
 * GovConstructionJobsPage — post-award kanban of gov construction jobs.
 * 20 stages. Drag-and-drop advances via /advance (server enforces checklist).
 * Route: /gov/construction
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import api from '../../services/api';

type Job = {
  id: string;
  stage: string;
  contract_number?: string | null;
  award_amount?: number | null;
  award_date?: string | null;
  period_of_performance_start?: string | null;
  period_of_performance_end?: string | null;
  solicitation_id?: string | null;
  contracting_officer_name?: string | null;
  agency_name?: string | null;
  job_site_address?: string | null;
  created_at?: string;
};

const STAGES: { key: string; label: string; color: string }[] = [
  { key: 'award_received', label: 'Award Received', color: '#3B82F6' },
  { key: 'contract_execution', label: 'Contract Execution', color: '#3B82F6' },
  { key: 'bonds_insurance', label: 'Bonds & Insurance', color: '#4F46E5' },
  { key: 'pre_construction_planning', label: 'Pre-Construction Planning', color: '#4F46E5' },
  { key: 'kickoff_meeting', label: 'Kickoff Meeting', color: '#6366F1' },
  { key: 'submittals_approvals', label: 'Submittals & Approvals', color: '#7C3AED' },
  { key: 'baseline_schedule', label: 'Baseline Schedule', color: '#7C3AED' },
  { key: 'safety_plan', label: 'Safety Plan', color: '#8B5CF6' },
  { key: 'notice_to_proceed', label: 'Notice to Proceed', color: '#A855F7' },
  { key: 'mobilization', label: 'Mobilization', color: '#EC4899' },
  { key: 'pre_construction_conference', label: 'Pre-Con Conference', color: '#EC4899' },
  { key: 'active_construction', label: 'Active Construction', color: '#EF4444' },
  { key: 'substantial_completion', label: 'Substantial Completion', color: '#F97316' },
  { key: 'punch_list', label: 'Punch List', color: '#F97316' },
  { key: 'final_inspection', label: 'Final Inspection', color: '#F59E0B' },
  { key: 'closeout_documents', label: 'Closeout Documents', color: '#F59E0B' },
  { key: 'final_pay_application', label: 'Final Pay App', color: '#EAB308' },
  { key: 'retention_release', label: 'Retention Release', color: '#84CC16' },
  { key: 'warranty_period', label: 'Warranty Period', color: '#22C55E' },
  { key: 'contract_closed', label: 'Closed', color: '#10B981' },
];

const styles: Record<string, React.CSSProperties> = {
  container: { padding: 24, background: '#0F1923', minHeight: '100vh', color: '#E5EDF5' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  title: { fontSize: 26, fontWeight: 800, margin: 0, color: '#fff' },
  subtitle: { color: '#8FA8C0', fontSize: 13, marginTop: 4 },
  board: { display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 24 },
  column: { minWidth: 260, background: '#1A2B3C', borderRadius: 10, padding: 10, display: 'flex', flexDirection: 'column', gap: 8 },
  columnHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 4px 10px', borderBottom: '1px solid #2D4259' },
  colTitle: { fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 },
  colCount: { fontSize: 11, background: '#243447', padding: '2px 8px', borderRadius: 10, color: '#8FA8C0' },
  card: {
    background: '#243447', borderRadius: 8, padding: 10, cursor: 'pointer',
    borderLeft: '3px solid #4A7EB5', color: '#E5EDF5', fontSize: 13,
    display: 'flex', flexDirection: 'column', gap: 4,
  },
  cardTitle: { fontWeight: 700, fontSize: 13 },
  cardMeta: { fontSize: 11, color: '#8FA8C0' },
  empty: { fontSize: 11, color: '#526980', fontStyle: 'italic', padding: 8 },
  loading: { color: '#8FA8C0', padding: 40, textAlign: 'center' as const },
};

const GovConstructionJobsPage: React.FC = () => {
  const nav = useNavigate();
  const [items, setItems] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/gov/construction-jobs');
        const arr = Array.isArray(data) ? data : data?.construction_jobs || data?.items || [];
        setItems(arr);
      } catch (e) {
        console.error('Failed to load construction jobs:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const byStage = useMemo(() => {
    const m: Record<string, Job[]> = {};
    for (const s of STAGES) m[s.key] = [];
    for (const j of items) {
      const k = j.stage || 'award_received';
      if (!m[k]) m[k] = [];
      m[k].push(j);
    }
    return m;
  }, [items]);

  const onDragStart = (e: React.DragEvent, id: string) => {
    e.dataTransfer.setData('text/plain', id);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDragOver = (e: React.DragEvent) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const onDrop = async (e: React.DragEvent, stageKey: string) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('text/plain');
    if (!id) return;
    const original = items;
    setItems((prev) => prev.map((j) => (j.id === id ? { ...j, stage: stageKey } : j)));
    try {
      await api.post(`/gov/construction-jobs/${id}/advance`, { to_stage: stageKey });
    } catch (err: any) {
      setItems(original);
      const resp = err?.response?.data;
      if (resp?.checklist_incomplete) {
        const unmet: string[] = resp.unmet_items || [];
        alert(
          `Checklist incomplete for current stage. Missing:\n• ${unmet.join('\n• ')}\n\nOpen the job to complete the checklist before advancing.`
        );
      } else {
        alert(`Failed to move: ${resp?.error || err?.message || 'unknown'}`);
      }
    }
  };

  const fmtMoney = (n?: number | null) => (n == null ? '' : `$${Math.round(n).toLocaleString()}`);
  const fmtDate = (s?: string | null) => { try { return s ? format(parseISO(s), 'MMM d') : ''; } catch { return ''; } };

  if (loading) return <div style={styles.container}><div style={styles.loading}>Loading construction jobs…</div></div>;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>🏗️ Construction Jobs</h1>
          <p style={styles.subtitle}>{items.length} active</p>
        </div>
      </div>

      <div style={styles.board}>
        {STAGES.map((s) => {
          const list = byStage[s.key] || [];
          return (
            <div
              key={s.key}
              style={{ ...styles.column, borderTop: `3px solid ${s.color}` }}
              onDragOver={onDragOver}
              onDrop={(e) => onDrop(e, s.key)}
            >
              <div style={styles.columnHeader}>
                <div style={styles.colTitle}>{s.label}</div>
                <div style={styles.colCount}>{list.length}</div>
              </div>
              {list.length === 0 && <div style={styles.empty}>No jobs</div>}
              {list.map((j) => (
                <div
                  key={j.id}
                  style={{ ...styles.card, borderLeftColor: s.color }}
                  draggable
                  onDragStart={(e) => onDragStart(e, j.id)}
                  onClick={() => nav(`/gov/construction/${j.id}`)}
                >
                  <div style={styles.cardTitle}>{j.contract_number || 'Contract # pending'}</div>
                  <div style={styles.cardMeta}>{j.agency_name || j.contracting_officer_name || 'Agency TBD'}</div>
                  {j.award_amount != null && <div style={styles.cardMeta}>{fmtMoney(j.award_amount)}</div>}
                  {j.period_of_performance_end && (
                    <div style={styles.cardMeta}>Ends {fmtDate(j.period_of_performance_end)}</div>
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GovConstructionJobsPage;
